import { useCallback, useEffect, useRef, useState } from "react";

// The sticky header sits below the app's fixed nav (headroom). Track its live
// bottom edge so the header offset follows the nav's pin/unpin motion.
const getStickyOffsetTop = () => {
  const headerBar =
    document.querySelector(".headroom-wrapper .first-row") ||
    document.querySelector(".headroom-wrapper");

  return headerBar ? Math.max(0, headerBar.getBoundingClientRect().bottom) : 0;
};

const syncStickyColumnWidths = (theadElement, stickyTableElement) => {
  const realThs = theadElement.querySelectorAll("th");
  const stickyThs = stickyTableElement.querySelectorAll("th");

  realThs.forEach((th, index) => {
    if (stickyThs[index]) {
      stickyThs[index].style.width = `${th.getBoundingClientRect().width}px`;
    }
  });
};

const useStickyTableHeader = ({ tableRef, wrapperRef, heading, rows }) => {
  const theadRef = useRef(null);
  const stickyHeaderRef = useRef(null);
  const stickyTableRef = useRef(null);
  const [stickyVisible, setStickyVisible] = useState(false);

  const updateStickyHeader = useCallback(() => {
    const tableElement = tableRef.current;
    const wrapperElement = wrapperRef.current;
    const theadElement = theadRef.current;
    const stickyWrapElement = stickyHeaderRef.current;
    const stickyTableElement = stickyTableRef.current;

    if (
      !tableElement ||
      !wrapperElement ||
      !theadElement ||
      !stickyWrapElement ||
      !stickyTableElement
    ) {
      return;
    }

    const offsetTop = getStickyOffsetTop();
    const tableRect = tableElement.getBoundingClientRect();
    const shouldShow =
      tableRect.top < offsetTop &&
      tableRect.bottom > offsetTop + theadElement.offsetHeight;

    setStickyVisible(shouldShow);
    if (!shouldShow) {
      return;
    }

    const wrapperRect = wrapperElement.getBoundingClientRect();
    stickyWrapElement.style.top = `${offsetTop}px`;
    stickyWrapElement.style.left = `${wrapperRect.left}px`;
    stickyWrapElement.style.width = `${wrapperRect.width}px`;

    stickyTableElement.style.width = `${tableElement.offsetWidth}px`;
    stickyTableElement.style.transform = `translateX(${-wrapperElement.scrollLeft}px)`;

    syncStickyColumnWidths(theadElement, stickyTableElement);
  }, [tableRef, wrapperRef]);

  useEffect(() => {
    updateStickyHeader();
    window.addEventListener("resize", updateStickyHeader);
    window.addEventListener("scroll", updateStickyHeader, true);

    return () => {
      window.removeEventListener("resize", updateStickyHeader);
      window.removeEventListener("scroll", updateStickyHeader, true);
    };
  }, [updateStickyHeader]);

  useEffect(() => {
    updateStickyHeader();
  }, [heading, rows, updateStickyHeader]);

  return {
    theadRef,
    stickyHeaderRef,
    stickyTableRef,
    stickyVisible,
    updateStickyHeader
  };
};

export default useStickyTableHeader;
